/**
 * Luật ĐỌC gói ván (`game:rounds`): xếp thứ tự gói, và soi tuổi con số trong gói.
 *
 * Gói về web qua HAI đường song song: vòng hỏi lại mỗi vài giây và frame SSE đẩy
 * tới 2 lần/giây trong lúc Máy bay đang bay. Hai đường KHÔNG có thứ tự với nhau —
 * một request hỏi đã bay đi từ trước hoàn toàn có thể hạ cánh SAU một frame SSE
 * mới hơn và mang theo ảnh chụp cũ hơn.
 *
 * Nên bên nhận phải tự xếp thứ tự, chứ không thể tin "cái tới sau là cái mới hơn".
 */

/**
 * Phần XẾP THỨ TỰ của một gói, như server gửi xuống.
 *
 * Nới thành `| undefined` dù hợp đồng kiểu khai `| null`, vì cả hai nguồn đều là
 * ép kiểu MÙ: frame SSE đi qua `JSON.parse(data) as JackpotEvent`, còn REST cũng
 * không validate. Một server bản cũ (gói còn trong TTL lúc vừa deploy) gửi thiếu
 * hẳn trường thì ở runtime nó là `undefined`, không phải `null`.
 */
export interface RoundsPacketOrder {
  rev?: number | null;
  epoch?: number | null;
  producerId?: string | null;
  /**
   * Hai trường NỘI DUNG, chỉ để nhận diện đúng một hình dạng: gói `disconnected`
   * rỗng do bên ĐỌC tự dựng. Xem nhánh "không xếp được thứ tự" trong
   * {@link acceptRoundsPacket} — không có chúng thì nhánh đó nhận bừa mọi gói lạ.
   */
  status?: string;
  games?: readonly unknown[];
}

/**
 * Mốc so ĐANG GIỮ. `rev` không nullable: chỉ gói XẾP ĐƯỢC thứ tự mới được làm mốc.
 */
export interface RoundsWatermark {
  rev: number;
  /** `null` = gói làm mốc không mang số thế hệ (bản observer cũ, hoặc `INCR` hỏng). */
  epoch: number | null;
  producerId: string | null;
}

export interface RoundsPacketDecision {
  /** Có thay `games` đang hiển thị bằng gói này không. */
  accept: boolean;
  /** Mốc so để GIỮ cho lượt sau — trả về cả khi bỏ gói, người gọi cứ gán thẳng. */
  watermark: RoundsWatermark | null;
}

/**
 * Xét một gói vừa nhận: nhận hay bỏ, và mốc so nào được giữ sau đó.
 *
 * Trả CẢ HAI trong một lần gọi là có chủ ý. Hai quyết định này dính nhau — nhận
 * gói mà không dời mốc, hoặc dời mốc theo một gói đã bỏ, đều mở lại đúng cái lỗ
 * mà cơ chế này sinh ra để bịt. Tách làm hai hàm là mời người gọi dùng lẻ một cái.
 *
 * Xếp thứ tự bằng `rev` (bộ đếm lượt ghi của observer) chứ KHÔNG bằng `updatedAt`:
 * mốc ISO chỉ xuống tới mili-giây, mà observer ghi nhiều lần đồng bộ trong cùng
 * một mili-giây (`markRolling()` rồi `captureFromEvent()` ở handler kết quả), nên
 * hai gói KHÁC nhau cùng mốc và phép so "mới hơn hẳn" đánh rơi gói thứ hai — đúng
 * cái gói mang mốc nổ. Mốc ISO còn đọc đồng hồ máy chủ nên NTP kéo lùi là tụt.
 *
 * Thứ tự giữa HAI luồng thì do `epoch` quyết — số thế hệ observer lấy bằng `INCR`
 * trên Redis lúc khởi động, nên nó tăng bền vững qua restart. `producerId` KHÔNG
 * làm được việc đó: nó là ID ngẫu nhiên, chỉ trả lời "có cùng luồng không". Bản
 * trước đọc "khác luồng ⇒ mới hơn", nên một gói TRỄ của luồng TRƯỚC (đã nhận
 * P2/rev 1, rồi một vòng hỏi mang P1/rev 900 hạ cánh) cũng thoả điều kiện đó và
 * kéo mốc so NGƯỢC về P1 — đúng thứ cả cơ chế này sinh ra để chặn.
 *
 * Các ca, theo thứ tự:
 *
 * 1. Gói KHÔNG xếp được thứ tự (`rev` vắng/không phải số) → chỉ nhận ĐÚNG MỘT hình
 *    dạng: gói `disconnected` RỖNG. Đó là gói do bên ĐỌC tự dựng khi khoá Redis
 *    vắng (`getRounds` trả `{ status: 'disconnected', rev: null, games: [] }` khi
 *    observer chết quá TTL hoặc chưa cấu hình tài khoản báo hũ) — nó không mang
 *    con số nào nên nhận vô hại, còn từ chối là treo sảnh ở khung hình cuối của
 *    một observer đã chết. Mốc thì GIỮ NGUYÊN: xoá mốc theo nó là ngay sau đó một
 *    vòng hỏi cũ đang bay hạ cánh, không còn gì để so, và nó dựng lại đúng mấy thẻ
 *    mà gói `disconnected` vừa xoá.
 *
 *    NHƯNG chỉ nhận khi mốc so KHÔNG đổi kể từ lúc lượt hỏi này BAY ĐI — xem
 *    {@link acceptRoundsPacket} tham số `watermarkAtRequest`. Đây là gói duy nhất
 *    được nhận mà không xếp được thứ tự, nên không chốt gì thì nó xoá được cả một
 *    thẻ MỚI HƠN vừa tới bằng đường kia trong lúc nó còn đang bay.
 *
 *    Mọi gói thiếu thứ tự KHÁC thì BỎ. Ca thật: một vòng hỏi đọc trúng gói ĐỊNH
 *    DẠNG CŨ (server chưa deploy xong, gói còn trong TTL) và hạ cánh SAU một
 *    frame SSE mới hơn. Nhánh này không soi `status`/`games` thì nó ghi đè thẻ mới
 *    bằng dữ liệu cũ — và giữ mốc cũng không cứu được, vì mốc chỉ chặn lần SAU chứ
 *    không rút lại lần ghi đè này. Giá phải trả cho việc bỏ: vài giây thẻ trống
 *    lúc deploy, tới khi observer bản mới ghi gói đầu tiên.
 * 2. Chưa có mốc → NHẬN và lấy mốc.
 * 3. CÙNG luồng → chỉ nhận khi `rev` TĂNG. `rev` tăng đúng 1 mỗi lượt ghi nên trong
 *    phạm vi một luồng nó là thứ tự chính xác nhất — chính xác hơn cả `epoch` (thứ
 *    đứng yên suốt đời tiến trình). Bằng nhau cũng bỏ: hỏi lại ra đúng ảnh chụp cũ
 *    thì không có gì để cập nhật, mà nhận thì mốc "vừa nhận" bị dời và tuổi của hệ
 *    số Máy bay trong gói bị tính trẻ đi.
 * 4. KHÁC luồng, cả hai bên đều có số thế hệ → so số thế hệ. Lớn hơn thắng, nhỏ hơn
 *    thua. Đây là ca thường: mọi gói của observer bản này đều mang `epoch`.
 * 5. KHÁC luồng, lệch nhau ở chỗ CÓ/KHÔNG có số thế hệ → đi được ĐÚNG MỘT CHIỀU:
 *    gói CÓ thế hệ hất được mốc KHÔNG có (đó là lúc observer bản mới thay bản cũ),
 *    còn chiều ngược lại thì không bao giờ. Chiều bị chặn chính là ca nguy hiểm:
 *    gói bản cũ còn nằm trong TTL không được phép hất một luồng đã xếp được thứ tự.
 * 6. KHÁC luồng mà KHÔNG bên nào xếp được (cả hai `epoch` rỗng, hoặc — về lý thuyết
 *    — trùng thế hệ) → NHẬN. Đây là ca duy nhất còn phải đoán, và chọn "nhận" vì
 *    phía kia là sảnh đứng hình VĨNH VIỄN: luồng cũ đã chết nên không bao giờ có
 *    gói nào hất được mốc của nó nữa, và người dùng không có cách nào khác ngoài
 *    tải lại trang. Giá phải trả tệ nhất ở đây chỉ là một khung hình cũ lọt qua
 *    (≤ 1 nhịp hỏi), và nó chỉ với tới được khi cả HAI luồng đều không có thế hệ —
 *    tức chỉ trong lúc còn sót observer bản cũ.
 *
 * @param watermarkAtRequest Mốc so CHỤP LÚC LƯỢT HỎI BẮT ĐẦU — chỉ đường REST
 * truyền, và chỉ ca 1 dùng tới. Mọi ca khác đã tự xếp được thứ tự bằng
 * `(epoch, rev)` nên không cần biết lượt hỏi bay đi từ lúc nào; riêng gói
 * `disconnected` rỗng thì KHÔNG có toạ độ nào trên trục đó, nên thứ duy nhất còn
 * lại để xét là "trong lúc mình đang bay, có gói nào mới hơn tới trước không".
 * Khác `current` = ĐÃ CÓ → gói này là tin cũ, bỏ. Bỏ trống (đường SSE đẩy thẳng,
 * không có độ trễ nào để lo) = không xét.
 */
export function acceptRoundsPacket(
  incoming: RoundsPacketOrder,
  current: RoundsWatermark | null,
  watermarkAtRequest?: RoundsWatermark | null,
): RoundsPacketDecision {
  const rev = incoming.rev;
  if (typeof rev !== 'number' || !Number.isFinite(rev)) {
    const raced =
      watermarkAtRequest !== undefined && !sameWatermark(current, watermarkAtRequest);
    return { accept: isEmptyDisconnected(incoming) && !raced, watermark: current };
  }

  const epoch =
    typeof incoming.epoch === 'number' && Number.isFinite(incoming.epoch) ? incoming.epoch : null;
  const watermark: RoundsWatermark = { rev, epoch, producerId: incoming.producerId ?? null };
  if (current === null) return { accept: true, watermark };

  if (current.producerId === watermark.producerId) {
    if (rev <= current.rev) return { accept: false, watermark: current };
    return { accept: true, watermark };
  }

  if (epoch !== null && current.epoch !== null && epoch !== current.epoch) {
    return epoch > current.epoch
      ? { accept: true, watermark }
      : { accept: false, watermark: current };
  }

  // Lệch nhau ở chỗ có/không có thế hệ: chỉ đi lên được, không đi xuống.
  if (epoch === null && current.epoch !== null) return { accept: false, watermark: current };

  return { accept: true, watermark };
}

/**
 * Đúng hình dạng gói `disconnected` rỗng mà `getRounds` tự dựng khi khoá Redis vắng.
 *
 * Soi CẢ HAI vế, vì `status` một mình không đủ: chính observer cũng ghi gói
 * `disconnected` MANG dữ liệu (`scheduleReconnect` ghi đè 4 thẻ để báo cho web biết
 * nó đã chết). Gói đó của bản hiện tại có `rev` nên không đi qua đây, nhưng gói cùng
 * loại do bản CŨ ghi thì rơi thẳng vào nhánh này.
 *
 * `games` rỗng mới là điều kiện làm việc nhận trở nên vô hại: không mang con số nào
 * thì không có gì để ghi đè nhầm lên thẻ đang đúng. Gói không xếp được thứ tự MÀ
 * còn mang dữ liệu thì đúng bằng "tin một ảnh chụp không biết chụp lúc nào".
 */
function isEmptyDisconnected(packet: RoundsPacketOrder): boolean {
  return (
    packet.status === 'disconnected' && Array.isArray(packet.games) && packet.games.length === 0
  );
}

/**
 * Hai mốc so có phải CÙNG MỘT lượt ghi không.
 *
 * So từng trường chứ không so tham chiếu: tham chiếu thì đúng với luồng gọi hiện
 * tại (mốc chỉ được dựng mới khi nhận gói) nhưng là một bất biến ngầm, và nó biến
 * hàm thuần này thành thứ không test nổi bằng hai object có cùng nội dung.
 */
function sameWatermark(a: RoundsWatermark | null, b: RoundsWatermark | null): boolean {
  if (a === null || b === null) return a === b;
  return a.rev === b.rev && a.epoch === b.epoch && a.producerId === b.producerId;
}

/**
 * Chốt tuổi cho ĐÚNG CON SỐ hệ số Máy bay — chặt hơn hẳn chốt tuổi cả gói.
 *
 * `MAX_PACKET_AGE_MS` (12s, ở `GameRoundCards`) đủ cho câu hỏi "còn đang bay
 * không", nhưng quá lỏng cho câu hỏi "con số này còn đúng không": hệ số đi theo
 * `e^(0.00006·ms)`, nên sau 12 giây hệ số THẬT đã gấp 2.05 lần con số đang hiện.
 * Số đúng nhưng cũ vẫn là báo ảo — tệ hơn ô trống, vì nó trông y hệt số đang chạy.
 *
 * 4 giây ≈ 1.5 lần nhịp THƯA NHẤT đo được (2593ms; trung bình 388ms). Đủ rộng để
 * một nhịp chậm bất thường không làm ô nhấp nháy, mà quá hạn thì hệ số thật cũng
 * mới lệch ~1.27 lần. Quá mốc này: hiện "—" chứ không hiện số cũ.
 */
export const MAX_ODD_AGE_MS = 4_000;

/**
 * Hệ số Máy bay trong gói còn dùng được không.
 *
 * Tuổi thật = tuổi LÚC SERVER GHI GÓI (`oddAgeMs`) + phần trôi kể từ lúc web nhận
 * gói. Thiếu vế đầu là đo nhầm đối tượng: `receivedAt` là mốc nhận CẢ GÓI VÁN, mà
 * ba game kia chuyển pha vài giây một lần và mỗi lần đó observer ghi một gói MỚI
 * mang `odd` Máy bay Y NGUYÊN. Chỉ đo vế sau thì mỗi lần Tài Xỉu/Xóc Đĩa đổi pha
 * là đồng hồ 4 giây được reset — tức chốt này gần như không bao giờ chạm, đúng
 * trong cửa sổ nguy hiểm `4s < tuổi < 10s` (trên 10s thì server mới tự hạ cờ
 * `flying` bằng `AVIATOR_TICK_STALE_MS`). Hệ số cũ 10 giây ở mốc 5x thì thật đã
 * ~9.1x.
 *
 * Cộng hai KHOẢNG THỜI GIAN chứ không trừ hai mốc epoch của hai máy khác nhau:
 * mỗi vế do đúng cái đồng hồ đo được nó tự đo, nên lệch giờ client/server không
 * lọt vào kết quả.
 *
 * Đổi lại, phép cộng đó BỎ SÓT đúng một khúc — thời gian gói nằm trong Redis / đi
 * trên đường, giữa lúc server ghi và lúc web nhận. Khúc đó không đo bằng khoảng
 * được (hai đầu thuộc hai đồng hồ khác nhau) nên nó có chốt riêng, thô hơn hẳn:
 * {@link isPacketStaleOnArrival}. Chia việc như vậy để mỗi chốt giữ đúng một phép
 * đo: hai chốt đo-theo-khoảng vẫn chính xác tới mili-giây, chốt so-đồng-hồ chỉ
 * đóng vai cầu dao cho gói cũ hàng chục giây.
 *
 * `oddAgeMs` vắng mặt = KHÔNG BIẾT tuổi → coi như cũ. Hai trường `odd`/`oddAgeMs`
 * do cùng một chỗ ghi ra nên vắng vế tuổi chỉ xảy ra với gói của server bản cũ;
 * lúc đó thà hiện "—" vài giây trong khi deploy còn hơn hiện một con số không
 * chứng minh được là mới.
 */
export function isOddFresh(oddAgeMs: number | undefined, sinceReceivedMs: number): boolean {
  if (typeof oddAgeMs !== 'number' || !Number.isFinite(oddAgeMs)) return false;
  return oddAgeMs + sinceReceivedMs <= MAX_ODD_AGE_MS;
}

/**
 * Trần cho TUỔI VẬN CHUYỂN: gói nằm trong Redis / trên đường truyền bao lâu trước
 * khi tới được trình duyệt.
 *
 * Đây là khoảng thời gian mà HAI chốt kia không ai đo: `oddAgeMs` dừng ở lúc
 * server GHI gói, còn `MAX_PACKET_AGE_MS`/phần `sinceReceivedMs` bắt đầu đếm từ
 * lúc web NHẬN gói. Khúc giữa rơi tự do. Ca hỏng thật: observer chết, gói cuối
 * vẫn sống trong Redis tới hết TTL; một trang VỪA MỞ hỏi REST và đọc trúng gói
 * cached đó → `sinceReceivedMs = 0`, `oddAgeMs` vẫn là ~0.3s ghi từ lúc đó → cả
 * hai chốt kia đều thấy "mới tinh", và thẻ vẽ hệ số của một chuyến bay đã nổ từ
 * lâu, giữ cờ `flying` thêm 12 giây nữa. Đúng cái báo ảo mà đợt này đi chống.
 *
 * 30 giây, và con số này buộc phải THÔ. Khác hai chốt kia ở bản chất phép đo:
 * chúng cộng hai KHOẢNG THỜI GIAN nên miễn nhiễm lệch đồng hồ, còn ở đây là trừ
 * mốc đồng hồ MÁY KHÁCH cho mốc đồng hồ MÁY CHỦ — sai số bằng đúng độ lệch giữa
 * hai đồng hồ, thứ không ai bảo đảm được. Nên nó chỉ làm được một việc: chặn gói
 * cũ HÀNG CHỤC GIÂY. Chuỗi số để chọn:
 *
 * - Độ trễ thật của một gói khoẻ: SSE vài chục ms, REST dưới một giây. Ngưỡng 30s
 *   là dư ~30 lần, cộng thêm chỗ cho vài giây lệch đồng hồ mà không nhấp nháy.
 * - Lệch ÂM (đồng hồ máy khách chậm hơn máy chủ) cho hiệu số âm → luôn nhỏ hơn
 *   ngưỡng → vô hại, không cần kẹp về 0.
 * - Lệch DƯƠNG lớn (máy khách nhanh hơn hàng phút — đồng hồ chưa đồng bộ) thì mọi
 *   gói đều bị coi là cũ. Chấp nhận có chủ ý: máy đó mất ô hệ số Máy bay, nhưng
 *   vẫn còn đủ 4 thẻ, mã phiên, hũ và đồng hồ đếm ngược. Đổi lại là chốt này
 *   KHÔNG được phép bỏ gói (xem chỗ gọi) — bỏ gói thì chính máy đó có sảnh trắng.
 * - Trần trên: TTL của `game:rounds` (90s) — đã hạ xuống chính vì chốt thô này
 *   không thể chặt hơn. Gói cached của một observer đã chết chỉ sống được tối đa
 *   90s, và quá 30s là chốt này bắt được.
 *
 * Còn sót cửa sổ 0–30 giây: gói cũ dưới 30s vẫn lọt. Hai chốt đo-theo-khoảng
 * nhận tiếp từ lúc nhận gói (hệ số tắt sau 4s, cờ bay sau 12s), nên hệ quả xấu
 * nhất là một thẻ hiện hệ số cũ trong ≤ 12 giây đầu sau khi mở trang, đúng vào
 * khoảng observer vừa chết chưa tới nửa phút.
 *
 * `updatedAt` vắng/hỏng → coi như CŨ, cùng chiều mặc định với `isOddFresh`: không
 * chứng minh được là mới thì không được hiện như số đang chạy. Gói duy nhất của
 * bản hiện tại rơi vào đó là gói `disconnected` do bên ĐỌC dựng — mà gói đó rỗng,
 * không có số nào để chặn.
 */
export const MAX_ARRIVAL_LAG_MS = 30_000;

/**
 * Gói này đã CŨ NGAY LÚC TỚI NƠI chưa — chốt THÔ, xem {@link MAX_ARRIVAL_LAG_MS}.
 *
 * @param updatedAt Mốc ISO server ghi gói (giờ MÁY CHỦ).
 * @param receivedAt Mốc lúc web nhận gói (giờ MÁY KHÁCH).
 */
export function isPacketStaleOnArrival(
  updatedAt: string | null | undefined,
  receivedAt: number,
): boolean {
  const writtenAt = typeof updatedAt === 'string' ? Date.parse(updatedAt) : Number.NaN;
  if (Number.isNaN(writtenAt)) return true;
  return receivedAt - writtenAt > MAX_ARRIVAL_LAG_MS;
}
